/**
 * Role-scoped project landing tab visibility and edit personas.
 */
import {
  ACCOUNTANT_TYPE_ID,
  DRAFTER_TYPE_ID,
  INSPECTOR_TYPE_ID,
  OSP_ENGINEERING_TYPE_ID,
  PERMITTING_MANAGER_TYPE_ID,
  PERMITTING_SPECIALIST_TYPE_ID,
  SALES_MANAGER_TYPE_ID,
  SPLICER_TYPE_ID,
  SPLICING_MANAGER_TYPE_ID,
  NETWORK_TECH_TYPE_ID,
  NETWORK_MANAGER_TYPE_ID,
  canViewerPermission,
  getViewerRoleType,
  hasEffectiveAdminPrivileges,
  isViewerAdministrator,
  isViewerCoordinator,
} from "./userMatrixStorage";
import { LANDING_TAB_VIEW_PERMISSION_KEYS } from "./userMatrixRoleLabels";

export const DISPATCHER_811_TYPE_ID = "role_811_dispatcher_pb01";

function normalizeMatrixRoleName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Match Permitting Manager / Specialist role names (tolerates common typos). */
export function isPermittingRoleName(name) {
  const n = normalizeMatrixRoleName(name);
  if (!n.includes("permitting")) return false;
  if (n.includes("manager")) return true;
  if (n.includes("specialist") || n.includes("specalist")) return true;
  return false;
}

export function isPermittingTabEditorRoleType(roleType) {
  if (!roleType) return false;
  const id = String(roleType.id || "");
  if (id === PERMITTING_MANAGER_TYPE_ID || id === PERMITTING_SPECIALIST_TYPE_ID) return true;
  return isPermittingRoleName(roleType.name);
}

export function isDrafterRoleType(roleType) {
  if (!roleType) return false;
  if (String(roleType.id || "") === DRAFTER_TYPE_ID) return true;
  return normalizeMatrixRoleName(roleType.name) === "drafter";
}

export function isNetworkTechRoleType(roleType) {
  if (!roleType) return false;
  if (String(roleType.id || "") === NETWORK_TECH_TYPE_ID) return true;
  const name = normalizeMatrixRoleName(roleType.name);
  if (!name.includes("network")) return false;
  if (name.includes("network manager")) return false;
  if (name.includes("network technician")) return true;
  if (name.includes("network tech")) return true;
  return name === "network";
}

export function isNetworkManagerRoleType(roleType) {
  if (!roleType) return false;
  if (String(roleType.id || "") === NETWORK_MANAGER_TYPE_ID) return true;
  return normalizeMatrixRoleName(roleType.name).includes("network manager");
}

export function isOspEngineeringRoleType(roleType) {
  if (!roleType) return false;
  if (String(roleType.id || "") === OSP_ENGINEERING_TYPE_ID) return true;
  const name = normalizeMatrixRoleName(roleType.name);
  // Match "OSP Engineer", "OSP Engineering", and close variants.
  return (
    name === "osp engineer" ||
    name === "osp engineering" ||
    name.includes("osp engineer")
  );
}

export function isSalesManagerRoleType(roleType) {
  if (!roleType) return false;
  if (String(roleType.id || "") === SALES_MANAGER_TYPE_ID) return true;
  const name = normalizeMatrixRoleName(roleType.name);
  if (name === "sales manager") return true;
  return name.includes("sales") && name.includes("manager");
}

export function isDispatcher811RoleType(roleType) {
  if (!roleType) return false;
  if (String(roleType.id || "") === DISPATCHER_811_TYPE_ID) return true;
  const name = normalizeMatrixRoleName(roleType.name);
  if (name === "811 dispatcher") return true;
  if (name.includes("811") && name.includes("dispatcher")) return true;
  return name.includes("usa dispatcher");
}

export function isInspectorRoleType(roleType) {
  if (!roleType) return false;
  if (String(roleType.id || "") === INSPECTOR_TYPE_ID) return true;
  const name = normalizeMatrixRoleName(roleType.name);
  return name === "inspector" || name.includes("inspector");
}

export function isAccountantRoleType(roleType) {
  if (!roleType) return false;
  if (String(roleType.id || "") === ACCOUNTANT_TYPE_ID) return true;
  const name = normalizeMatrixRoleName(roleType.name);
  return name === "accountant" || name.includes("accountant");
}

export function isSplicerRoleType(roleType) {
  if (!roleType) return false;
  if (String(roleType.id || "") === SPLICER_TYPE_ID) return true;
  return normalizeMatrixRoleName(roleType.name) === "splicer";
}

export function isSplicingManagerRoleType(roleType) {
  if (!roleType) return false;
  if (String(roleType.id || "") === SPLICING_MANAGER_TYPE_ID) return true;
  const name = normalizeMatrixRoleName(roleType.name);
  if (name === "splicing manager" || name === "splicer manager") return true;
  return name.includes("splic") && name.includes("manager");
}

const CONSTRUCTION_FIELD_ROLE_PATTERNS = [
  "construction manager",
  "construction laborer",
  "construction lead",
];

export function isConstructionManagerRoleType(roleType) {
  if (!roleType) return false;
  const name = normalizeMatrixRoleName(roleType.name);
  return name === "construction manager" || name.includes("construction manager");
}

export function isConstructionLeadRoleType(roleType) {
  if (!roleType) return false;
  const name = normalizeMatrixRoleName(roleType.name);
  return name === "construction lead" || name.includes("construction lead");
}

export function isConstructionFieldRoleType(roleType) {
  if (!roleType) return false;
  const name = normalizeMatrixRoleName(roleType.name);
  return CONSTRUCTION_FIELD_ROLE_PATTERNS.some((p) => name === p || name.includes(p));
}

/** @returns {"full"|"permitting"|"drafter"|"ospEngineering"|"sales"|"dispatcher811"|"inspector"|"accountant"|"splicer"|"splicingManager"|"networkTech"|"constructionField"|"default"} */
export function resolveViewerLandingPersona(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return "full";
  if (isViewerAdministrator(viewerEmail) || isViewerCoordinator(viewerEmail)) return "full";
  const role = getViewerRoleType(viewerEmail);
  if (!role) return "default";
  if (isPermittingTabEditorRoleType(role)) return "permitting";
  if (isDrafterRoleType(role)) return "drafter";
  if (isOspEngineeringRoleType(role)) return "ospEngineering";
  if (isSalesManagerRoleType(role)) return "sales";
  if (isDispatcher811RoleType(role)) return "dispatcher811";
  if (isInspectorRoleType(role)) return "inspector";
  if (isAccountantRoleType(role)) return "accountant";
  if (isSplicingManagerRoleType(role)) return "splicingManager";
  if (isSplicerRoleType(role)) return "splicer";
  if (isNetworkTechRoleType(role)) return "networkTech";
  if (isConstructionFieldRoleType(role)) return "constructionField";
  return "default";
}

const PERSONA_HIDDEN_TAB_IDS = {
  permitting: new Set(["engineering", "construction", "fiber", "networking"]),
  drafter: new Set(["permitting", "construction", "fiber", "networking"]),
  /** Construction hidden only on commercial (see filterLandingTabsForViewer). */
  ospEngineering: new Set(["permitting", "fiber", "networking", "accounting"]),
  sales: new Set(["engineering", "permitting", "construction", "fiber", "networking", "accounting"]),
  dispatcher811: new Set(["engineering", "permitting", "fiber", "networking", "accounting"]),
  inspector: new Set(["engineering", "fiber", "networking", "accounting"]),
  accountant: new Set(["engineering", "permitting", "construction", "fiber", "networking"]),
  splicer: new Set(["engineering", "permitting", "construction", "networking", "accounting"]),
  networkTech: new Set(["engineering", "permitting", "construction", "fiber", "accounting"]),
  constructionField: new Set(["engineering", "permitting", "fiber", "networking", "accounting"]),
};

function viewerMayViewLandingTab(tabId, viewerEmail) {
  const key = LANDING_TAB_VIEW_PERMISSION_KEYS[tabId];
  if (!key) return true;
  // Overview defaults on; other tabs default on when the key is unset (legacy roles).
  const defaultWhenUnknown = true;
  return canViewerPermission(viewerEmail, key, defaultWhenUnknown);
}

/** @param {{ commercial?: boolean }} [options] — commercial OSP Engineer also hides Construction. */
export function filterLandingTabsForViewer(tabs, viewerEmail, options = {}) {
  const list = Array.isArray(tabs) ? tabs : [];
  const persona = resolveViewerLandingPersona(viewerEmail);
  const hidden = PERSONA_HIDDEN_TAB_IDS[persona];
  return list.filter((tab) => {
    if (
      persona === "ospEngineering" &&
      options.commercial &&
      tab.id === "construction"
    ) {
      return false;
    }
    // User Matrix "Can view X tab" wins over persona defaults. Persona hides apply only when
    // the matching permission key is unset (legacy matrices before tab-view permissions).
    const permKey = LANDING_TAB_VIEW_PERMISSION_KEYS[tab.id];
    if (permKey) {
      const role = getViewerRoleType(viewerEmail);
      if (role?.permissions && Object.prototype.hasOwnProperty.call(role.permissions, permKey)) {
        return Boolean(role.permissions[permKey]);
      }
    }
    if (hidden?.has(tab.id)) return false;
    if (!viewerMayViewLandingTab(tab.id, viewerEmail)) return false;
    return true;
  });
}

export function primaryEditTabForPersona(viewerEmail) {
  const persona = resolveViewerLandingPersona(viewerEmail);
  if (persona === "permitting") return "permitting";
  if (persona === "sales") return "summary";
  if (persona === "drafter" || persona === "ospEngineering") return "engineering";
  if (
    persona === "inspector" ||
    persona === "constructionField" ||
    persona === "dispatcher811"
  ) {
    return "construction";
  }
  if (persona === "accountant") return "accounting";
  if (persona === "splicer" || persona === "splicingManager") return "fiber";
  if (persona === "networkTech") return "networking";
  return null;
}

/** Tabs a scoped persona may edit; null means all visible tabs. */
export function editableTabsForPersona(viewerEmail) {
  const persona = resolveViewerLandingPersona(viewerEmail);
  if (persona === "permitting") return new Set(["summary", "permitting"]);
  if (persona === "sales") return new Set(["summary"]);
  if (persona === "drafter" || persona === "ospEngineering") return new Set(["engineering"]);
  if (persona === "inspector" || persona === "constructionField" || persona === "dispatcher811") {
    return new Set(["construction"]);
  }
  if (persona === "accountant") return new Set(["accounting"]);
  if (persona === "networkTech") return new Set(["networking"]);
  if (persona === "splicingManager") return new Set(["fiber"]);
  return null;
}
